import React, {useEffect, useRef} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faCircleQuestion, faTriangleExclamation} from '@fortawesome/free-solid-svg-icons';
import {Modal} from './Modal';

/**
 * Hộp xác nhận dùng chung - phần NHÌN THẤY.
 * ---------------------------------------------------------------------------
 * Trước đây mỗi chỗ tự gọi `window.confirm` với một chuỗi dài nối bằng `\n` và
 * dấu `•` gõ tay. Hộp thoại của trình duyệt có ba vấn đề, và cái thứ ba mới là
 * cái đắt nhất:
 *
 *   1. Nó không phải app: font hệ thống, nút tiếng Anh trên máy tính, trên
 *      Chrome còn kèm dòng tên miền phía trên câu hỏi.
 *   2. Nó nuốt hết định dạng: tiêu đề, số tiền, danh sách hệ quả và câu cảnh
 *      báo "không hoàn tác" trôi thành một khối chữ xám như nhau, nên thứ cần
 *      đọc kỹ nhất lại chìm giữa những thứ chỉ cần liếc.
 *   3. Nó KHOÁ cả trang một cách đồng bộ. Chủ nhà đang đứng trước câu
 *      "Chốt trả phòng 201?" thì không cuộn lại được để xem số tiền cọc đang
 *      hiện ngay phía sau - trong khi đó đúng là thứ họ cần nhìn để trả lời.
 *
 * Nên các trường ở đây không phải để trang trí: `subtitle` giữ tên phòng và số
 * tiền tách khỏi câu hỏi, `sections` giữ nguyên các nhóm hệ quả (cái được trả
 * lại / cái mất hẳn), `note` là dòng cuối cùng đọc trước khi bấm.
 *
 * Chỗ gọi không dựng component này trực tiếp - dùng `useConfirm()` trong
 * contexts/ConfirmContext.tsx, chỉ khác `window.confirm` ở chữ `await`.
 */

export interface ConfirmSection {
    /** Nhãn nhóm - vd "TRẢ LẠI", "KHÔNG LẤY LẠI ĐƯỢC" */
    label?: string;
    /** danger = viết bằng màu cảnh báo, cho nhóm hệ quả không lùi được */
    tone?: 'neutral' | 'danger';
    items: React.ReactNode[];
}

export interface ConfirmOptions {
    /** Câu hỏi. Một dòng, có dấu hỏi ở cuối. */
    title: React.ReactNode;
    /**
     * Dòng nhận dạng đặt ngay dưới câu hỏi: TÊN PHÒNG và SỐ TIỀN.
     *
     * Chủ nhà nhớ người thuê và số tiền chứ không nhớ mã phòng, và mọi thao tác
     * đụng tới tiền đều làm trên một danh sách nhiều phòng giống nhau - dòng
     * này là chỗ họ đối chiếu "đúng phòng mình vừa bấm chưa".
     */
    subtitle?: React.ReactNode;
    /** Một đoạn nói rõ chuyện gì sắp xảy ra */
    description?: React.ReactNode;
    /** Danh sách hệ quả - viết tắt của `sections: [{items: bullets}]` */
    bullets?: React.ReactNode[];
    /** Khi hệ quả chia thành nhóm (được trả lại / mất hẳn) */
    sections?: ConfirmSection[];
    /** Dòng cuối, chữ nhỏ - vd "Không khôi phục lại được." */
    note?: React.ReactNode;
    /** danger = thao tác xoá/không hoàn tác. Mặc định primary. */
    tone?: 'primary' | 'danger';
    confirmLabel?: string;
    cancelLabel?: string;
    icon?: IconDefinition;
}

interface ConfirmDialogProps {
    options: ConfirmOptions;
    onConfirm: () => void;
    onCancel: () => void;
}

const TONE = {
    primary: {
        icon: faCircleQuestion,
        badge: 'bg-violet-500/15 text-violet-600 dark:bg-violet-400/15 dark:text-violet-300',
        button: 'bg-violet-600 hover:bg-violet-700',
        label: 'Xác nhận',
    },
    danger: {
        icon: faTriangleExclamation,
        badge: 'bg-rose-500/15 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300',
        button: 'bg-rose-600 hover:bg-rose-700',
        label: 'Xác nhận',
    },
} as const;

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({options, onConfirm, onCancel}) => {
    const {
        title,
        subtitle,
        description,
        bullets,
        sections,
        note,
        tone = 'primary',
        confirmLabel,
        cancelLabel = 'Hủy',
        icon,
    } = options;

    const skin = TONE[tone];
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    /* Thao tác nguy hiểm thì con trỏ đậu ở nút Hủy: hộp thoại này hay bật lên
       giữa lúc người dùng đang gõ, và một phím Enter theo quán tính không được
       phép xoá bản ghi của cả một tháng. Thao tác thường thì ngược lại - đậu ở
       nút đồng ý để trả lời xong bằng đúng một phím. */
    useEffect(() => {
        const target = tone === 'danger' ? cancelRef.current : confirmRef.current;
        target?.focus();
    }, [tone]);

    const groups: ConfirmSection[] = [
        ...(bullets && bullets.length > 0 ? [{items: bullets}] : []),
        ...(sections || []),
    ].filter(group => group.items.length > 0);

    return (
        <Modal
            isOpen
            onClose={onCancel}
            size="md"
            mobile="dialog"
            layer="top"
            showClose={false}
            labelledBy="confirm-title"
            footer={
                <div className="flex gap-2">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-3 rounded-lg glass-btn text-sm font-medium"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={onConfirm}
                        className={`flex-1 px-4 py-3 rounded-lg text-sm font-semibold text-white
                            shadow-sm transition-all ${skin.button}`}
                    >
                        {confirmLabel || skin.label}
                    </button>
                </div>
            }
        >
            <div className="flex gap-3.5">
                <span
                    className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center ${skin.badge}`}
                    aria-hidden="true"
                >
                    <FontAwesomeIcon icon={icon || skin.icon}/>
                </span>

                <div className="min-w-0 flex-1 space-y-3">
                    <div>
                        <h2 id="confirm-title" className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="mt-0.5 text-sm font-medium text-slate-600 dark:text-slate-300">
                                {subtitle}
                            </p>
                        )}
                    </div>

                    {description && (
                        <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">
                            {description}
                        </p>
                    )}

                    {groups.map((group, index) => (
                        <div key={index} className="glass-soft px-3 py-2.5">
                            {group.label && (
                                <p className={`text-[11px] font-semibold uppercase tracking-wider mb-1.5
                                    ${group.tone === 'danger'
                                        ? 'text-rose-600 dark:text-rose-300'
                                        : 'text-slate-500 dark:text-slate-400'}`}>
                                    {group.label}
                                </p>
                            )}
                            <ul className={`space-y-1 text-sm ${group.tone === 'danger'
                                ? 'text-rose-700 dark:text-rose-200'
                                : 'text-slate-700 dark:text-slate-200'}`}>
                                {group.items.map((item, itemIndex) => (
                                    <li key={itemIndex} className="flex gap-2">
                                        <span aria-hidden="true" className="select-none opacity-60">•</span>
                                        <span className="min-w-0">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                    {note && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 whitespace-pre-line">
                            {note}
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
};
